/**
 * @module M/control/ReferenceLocatorCatastroControl
 */

import ReferenceLocatorCatastroImplControl from 'impl/referencelocatorcatastrocontrol';
import template from 'templates/referencelocatorcatastro';
import { getValue } from './i18n/language';


export default class ReferenceLocatorCatastroControl extends M.Control {
  /**
   * @classdesc
   * Main constructor of the class. Creates a PluginControl
   * control
   *
   * @constructor
   * @extends {M.Control}
   * @api stable
   */
  constructor(parameters) {
    if (M.utils.isUndefined(ReferenceLocatorCatastroImplControl)) {
      M.exception(getValue('exception.impl'));
    }

    const impl = new ReferenceLocatorCatastroImplControl();
    super(impl, 'ReferenceLocatorCatastro');
    if (parameters) {
      this.options = (parameters || {});
      this.config_ = (parameters || {});
    } else {
      M.exception(getValue('exception.config'));
    }

    if (M.utils.isUndefined(this.options.name) || M.utils.isNullOrEmpty(this.options.name)) {
      this.options.name = this.NAME || 'ReferenceLocatorCatastro';
    }

    this.facadeMap_ = null;

    /**
     * Url for "consulta de coordenadas por Provincia, Municipio y Referencia Catastral"
     * @private
     * @type {String}
     */
    this.CPMRC_url_ = this.config_.CPMRC_url;

    /**
     * Url for "consulta de Referencia Catastral por Coordenadas"
     * @private
     * @type {String}
     */
    this.RCCOOR_url_ = this.config_.RCCOOR_url;

    /**
     * Container of the results to scroll
     * @private
     * @type {HTMLElement}
     */
    this.resultsScrollContainer_ = null;

    /**
     * Timestamp of the search to abort old requests
     * @private
     * @type {Nunber}
     */
    this.searchTime_ = 0;

    /**
     * Results of the search
     * @private
     * @type {Array<Object>}
     */
    this.rcResults_ = [];

    /**
     * Flag that indicates the scroll is up
     * @private
     * @type {Boolean}
     */
    this.scrollIsUp_ = true;

    /**
     * Draggable panel to show results
     * @private
     * @type {Object}
     */
    this.draggablePanel = null;

    /**
     * Main control's html element
     * @private
     * @type {HTMLElement}
     */
    this.element_ = null;

    /**
     * Name of searching css class
     * @const
     * @type {string}
     * @public
     * @api stable
     */
    this.SEARCHING_CLASS = 'm-searching';

    /**
     * Name of hidden css class
     * @const
     * @type {string}
     * @public
     * @api stable
     */
    this.HIDDEN_RESULTS_CLASS = 'hidden';

    /**
     * Title for the popup
     * @const
     * @type {string}
     * @public
     * @api stable
     */
    this.POPUP_TITLE = getValue('info');

    /**
     * Result's layer. Invisible on TOC.
     * @private
     * @type {M.layer}
     */
    this.layer_ = new M.layer.GeoJSON({ name: getValue('locator'), crs: '3857' });
  }

  /**
   * This function adds events listeners to html elements
   *
   * @public
   * @function
   * @param {HTMLElement} element
   * @api stable
   */
  addEvents_(html) {
    this.element_ = html;
  }

  /**
   * This function creates the view
   *
   * @public
   * @function
   * @param {M.Map} map to add the control
   * @api stable
   */
  createView(map) {
    this.facadeMap_ = map;
    this.facadeMap_.addLayers(this.layer_);
    this.layer_.displayInLayerSwitcher = false;
    return new Promise((success, fail) => {
      const html = M.template.compileSync(template, {
        jsonp: true,
        vars: {
          translations: {
            tooltip: getValue('tooltip'),
            query_data: getValue('query_data'),
            clean_results: getValue('clean_results'),
            placeholder: getValue('placeholder'),
            clean: getValue('clean'),
            search: getValue('search'),
            reference: getValue('reference'),
          },
        },
      });
      this.setStyle(html, this.options);
      this.element_ = html;
      this.createCatastroPanel();
      this.addEvents_(this.element_);
      this.checkForExistingElement();
      success(html);
    });
  }

  /**
   * This function compiles the template and makes a panel
   *
   * @public
   * @function
   * @api stable
   */
  createCatastroPanel() {
    const castastroInfo = this.element_.querySelector('#m-catastrogetinfo-button');
    castastroInfo.addEventListener('click', this.activeInfo.bind(this));

    const buttonClear = this.element_.querySelector('#m-catastroclear-button');
    buttonClear.addEventListener('click', this.clear.bind(this));
  }

  /**
   * This function sets user defined style options to button
   *
   * @public
   * @function
   * @api stable
   */
  setStyle(html, options) {
    if (!M.utils.isUndefined(options.tooltip) && !M.utils.isNullOrEmpty(options.tooltip)) {
      html.querySelector('button').setAttribute('title', options.tooltip);
    }
    if (!M.utils.isUndefined(options.icon) && !M.utils.isNullOrEmpty(options.icon)) {
      html.querySelector('button').setAttribute('class', options.icon);
    }
    if (!M.utils.isUndefined(options.order) && !M.utils.isNullOrEmpty(options.order)) {
      html.querySelector('button').parentNode.setAttribute('style', `order: ${options.order}`);
    }
  }

  /**
   * Set catastro info button active
   *
   * @public
   * @function
   * @api stable
   */
  activeInfo() {
    this.activate();
  }

  /**
   * This function builds the query URL and shows results
   *
   * @private
   * @function
   * @param {ol.MapBrowserPointerEvent} evt - Browser point event
   */
  buildUrl_(evt) {
    const options = {
      jsonp: true,
    };

    const srs = this.facadeMap_.getProjection().code;
    M.remote.get(this.RCCOOR_url_, {
      SRS: srs,
      Coordenada_X: evt.coord[0],
      Coordenada_Y: evt.coord[1],
    }).then((res) => {
      this.showInfoFromURL_(res, evt.coord);
    }, options);
  }

  /**
   * This function displays information in a popup
   *
   * @private
   * @function
   * @param {XML} response - response from the petition
   * @param {array} coordinate - Coordinate position onClick
   */
  showInfoFromURL_(response, coordinates) {
    if ((response.code === 200) && (response.error === false)) {
      const infos = [];
      const info = response.text;
      const responseInfo = this.formatInfo_(info);
      const formatedInfo = responseInfo.tabContent;
      const reference = responseInfo.reference;
      if (formatedInfo === null) {
        M.dialog.info(getValue('no_results'));
        setTimeout(() => {
          document.querySelector('div.m-mapea-container div.m-dialog div.m-title').style.backgroundColor = '#71a7d3';
          const button = document.querySelector('div.m-dialog.info div.m-button > button');
          button.innerHTML = getValue('close');
          button.style.width = '75px';
          button.style.backgroundColor = '#71a7d3';
        }, 5);
      } else {
        const searchUrl = this.CPMRC_url_.split('*').join(reference);
        M.remote.get(searchUrl).then((responseGeometry) => {
          try {
            if ((responseGeometry.code === 200) && (responseGeometry.error === false)) {
              const features = this.getImpl().readGeometry(responseGeometry.text);
              features.forEach((f) => {
                f.setAttribute('reference', reference);
              });

              if (features.length > 0) {
                this.showResults_.call(this, features);
              } else {
                M.dialog.error(getValue('exception.parsing'));
              }
            } else {
              M.dialog.error(getValue('exception.connecting'));
            }
          } catch (err) {
            M.dialog.info(getValue('no_results'));
            setTimeout(() => {
              document.querySelector('div.m-mapea-container div.m-dialog div.m-title').style.backgroundColor = '#71a7d3';
              const button = document.querySelector('div.m-dialog.info div.m-button > button');
              button.innerHTML = getValue('close');
              button.style.width = '75px';
              button.style.backgroundColor = '#71a7d3';
            }, 5);
          }
        });

        infos.push(formatedInfo);
        const tab = {
          icon: 'g-cartografia-pin',
          title: this.POPUP_TITLE,
          content: infos.join(''),
        };

        let popup = this.facadeMap_.getPopup();
        if (M.utils.isNullOrEmpty(popup)) {
          popup = new M.Popup();
          popup.addTab(tab);
          this.facadeMap_.addPopup(popup, coordinates);
        } else if (popup.getCoordinate()[0] === coordinates[0] && popup.getCoordinate()[1] === coordinates[1]) {
          let hasExternalContent = false;
          popup.getTabs().forEach((t) => {
            if (t.title !== this.POPUP_TITLE) {
              hasExternalContent = true;
            } else {
              popup.removeTab(t);
            }
          });
          if (hasExternalContent) {
            popup.addTab(tab);
          } else {
            popup = new M.Popup();
            popup.addTab(tab);
            this.facadeMap_.addPopup(popup, coordinates);
          }
        } else {
          popup = new M.Popup();
          popup.addTab(tab);
          this.facadeMap_.addPopup(popup, coordinates);
        }
      }
    } else {
      this.facadeMap_.removePopup();
      M.dialog.error(getValue('exception.connecting'));
    }
  }

  /**
   * This function formats the response
   *
   * @param {string} info - Information to formatting
   * @returns {string} information - Formatted information
   * @private
   * @function
   */
  formatInfo_(info) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(info, 'text/xml');
    let res = { tabContent: null, reference: null };
    let ldtNode;
    let valuePopup;
    let codProv;
    let codMun;
    const rootElement = xmlDoc.getElementsByTagName('consulta_coordenadas')[0];
    const controlNode = rootElement.getElementsByTagName('control')[0];
    const errorCtlNode = controlNode.getElementsByTagName('cuerr')[0].childNodes[0].nodeValue;
    if (errorCtlNode === '1') {
      const errorNode = rootElement.getElementsByTagName('lerr')[0];
      const errorDesc = errorNode.getElementsByTagName('err')[0];
      const errorDescTxt = errorDesc.getElementsByTagName('des')[0].childNodes[0].nodeValue;
      valuePopup = errorDescTxt;
    } else {
      const coordenadasNode = rootElement.getElementsByTagName('coordenadas')[0];
      const coordNode = coordenadasNode.getElementsByTagName('coord')[0];
      const pcNode = coordNode.getElementsByTagName('pc')[0];
      const pc1Node = pcNode.getElementsByTagName('pc1')[0].childNodes[0].nodeValue;
      const pc2Node = coordNode.getElementsByTagName('pc2')[0].childNodes[0].nodeValue;
      codProv = pc1Node.substring(0, 2);
      codMun = pc1Node.substring(2, 5);
      ldtNode = coordNode.getElementsByTagName('ldt')[0].childNodes[0].nodeValue;
      valuePopup = pc1Node + pc2Node;
      const link = `https://www1.sedecatastro.gob.es/CYCBienInmueble/OVCConCiud.aspx?UrbRus=&RefC=${valuePopup}&esBice=&RCBice1=&RCBice2=&DenoBice=&from=OVCBusqueda&pest=rc&RCCompleta=${valuePopup}&final=&del=${codProv}&mun=${codMun}`;
      const formatedInfo = `<span class="divinfo-title">${getValue('info')}</span>
      <div class='divinfo'>
      <table class='mapea-table'>
      <tbody>
      <tr><td colspan='4'></td></tr>
      <tr><td class='key'><b>${M.utils.beautifyAttribute(getValue('reference'))}</b></td><td class='value'></b>
      <a href='${link}' target='_blank'>${valuePopup}</a></td></tr>
      <tr><td class='key'><b>${M.utils.beautifyAttribute(getValue('description'))}</b></td>
      <td class='value'>${ldtNode}</td></tr>
      </tbody></table>
      <div class="m-referencelocatorcatastro-select-geometry">
        <button id="m-referencelocatorcatastro-select-geometry">${getValue('select')}</button>
      </div>
      </div>`;
      res = { tabContent: formatedInfo, reference: valuePopup };
    }

    return res;
  }

  /**
   * This function parses results and compiles template
   * with vars to show results
   *
   * @private
   * @function
   */
  showResults_(features) {
    this.getLayer().clear();
    this.layer_.addFeatures(features);
    this.layer_.setStyle(new M.style.Polygon({
      fill: {
        color: '#71a7d3',
        opacity: 0.2,
      },
      stroke: {
        color: '#71a7d3',
        width: 6,
      },
    }));


    setTimeout(() => {
      this.zoomToResults();
      document.querySelector('#m-referencelocatorcatastro-select-geometry').addEventListener('click', () => {
        this.sendGeometryToForm(features);
      });
    }, 200);
  }

  sendGeometryToForm(features) {
    try {
      const finalFeatures = [];
      features.forEach((f) => {
        finalFeatures.push(f.getGeoJSON());
      });

      const geojson = {
        type: 'FeatureCollection',
        features: finalFeatures,
      };

      const arrayParams = window.location.search.split('&');
      let idInput = '';
      let parentURL = '';
      for (let i = 0; i < arrayParams.length; i += 1) {
        if (arrayParams[i].startsWith('ggisQuestionId') || arrayParams[i].startsWith('?ggisQuestionId')) {
          idInput = arrayParams[i].replace('?', '');
          idInput = idInput.replace('ggisQuestionId=', '');
        }
      }

      for (let i = 0; i < arrayParams.length; i += 1) {
        if (arrayParams[i].startsWith('parentURL') || arrayParams[i].startsWith('?parentURL')) {
          parentURL = arrayParams[i].replace('?', '');
          parentURL = parentURL.replace('parentURL=', '');
        }
      }

      const stringToSend = `${idInput}:${JSON.stringify(geojson)}`;
      window.parent.postMessage(stringToSend, decodeURIComponent('https://demos.guadaltel.es/gforms-goncev3/formulario.jsf'));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }

  checkForExistingElement() {
    setTimeout(() => {
      const arrayParams = window.location.search.split('&');
      let idInput = '';
      for (let i = 0; i < arrayParams.length; i += 1) {
        if (arrayParams[i].startsWith('ggisQuestionId') || arrayParams[i].startsWith('?ggisQuestionId')) {
          idInput = arrayParams[i].replace('?', '');
          idInput = idInput.replace('ggisQuestionId=', '');
        }
      }

      if (idInput !== '') {
        try {
          let textValue = window.parent.document.getElementById(`formRenderer:${idInput}`).value;
          textValue = textValue.split('&quot;').join('"');
          if (textValue !== '') {
            const geojson = JSON.parse(textValue);
            const features = geojson.features.map((f) => {
              return new M.Feature(f.id, f);
            });

            this.layer_.addFeatures(features);
            setTimeout(() => {
              this.zoomToResults();
              this.layer_.setStyle(new M.style.Polygon({
                fill: {
                  color: '#71a7d3',
                  opacity: 0.2,
                },
                stroke: {
                  color: '#71a7d3',
                  width: 6,
                },
              }));
            }, 100);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      }
    }, 500);
  }

  /**
   * This function zooms the view to the results on layer
   *
   * @public
   * @function
   * @param {M.Map} map to add the control
   * @api stable
   */
  zoomToResults() {
    const bbox = this.layer_.getFeaturesExtent();
    this.facadeMap_.setBbox(bbox);
  }

  /**
   * This function returns the layer used
   *
   * @public
   * @function
   * @returns {ol.layer.Vector}
   * @api stable
   */
  getLayer() {
    return this.layer_;
  }

  /**
   * This function clears the layer
   *
   * @public
   * @function
   * @param {M.Map} map to add the control
   * @api stable
   */
  clear() {
    this.facadeMap_.removePopup();
    this.layer_.clear();
  }

  /**
   * This function is called on the control activation
   *
   * @public
   * @function
   * @api stable
   */
  activate() {
    if (this.activated) {
      this.deactivate();
    } else {
      document.body.style.cursor = 'crosshair';
      let someCtlActive = false;
      const controls = this.facadeMap_.getControls();
      controls.some((control) => {
        if (control.activated === true && !(control instanceof this)) {
          M.dialog.info(getValue('exception.deactivate').split('*').join(control.name));
          someCtlActive = true;
          setTimeout(() => {
            document.querySelector('div.m-mapea-container div.m-dialog div.m-title').style.backgroundColor = '#71a7d3';
            const button = document.querySelector('div.m-dialog.info div.m-button > button');
            button.innerHTML = getValue('close');
            button.style.width = '75px';
            button.style.backgroundColor = '#71a7d3';
          }, 5);
        }

        return someCtlActive;
      });

      if (someCtlActive === false) {
        this.facadeMap_.on(M.evt.CLICK, this.buildUrl_, this);
        this.activated = true;
        this.element_.querySelector('#m-catastrogetinfo-button').classList.add('activated');
      } else {
        this.activated = false;
      }
    }
  }

  /**
   * This function is called on the control deactivation
   *
   * @public
   * @function
   * @api stable
   */
  deactivate() {
    document.body.style.cursor = '';
    this.facadeMap_.removePopup();
    this.facadeMap_.un(M.evt.CLICK, this.buildUrl_, this);
    this.activated = false;
    this.element_.querySelector('#m-catastrogetinfo-button').classList.remove('activated');
  }
  /**
   * This function gets activation button
   *
   * @public
   * @function
   * @param {HTML} html of control
   * @api stable
   */
  getActivationButton(html) {
    return html.querySelector('.m-referencelocatorcatastro button');
  }

  /**
   * This function compares controls
   *
   * @public
   * @function
   * @param {M.Control} control to compare
   * @api stable
   */
  equals(obj) {
    let equals = false;
    if (obj instanceof ReferenceLocatorCatastroControl) {
      equals = (this.name === obj.name);
    }
    return equals;
  }

  /**
   * This function destroys this plugin
   *
   * @public
   * @function
   * @api stable
   */
  destroy() {
    this.clear();
    this.facadeMap_.areasContainer.querySelector('.m-top.m-right').classList.remove('top-extra');
    this.facadeMap_.getMapImpl().removeControl(this);
    this.facadeMap_ = null;
    this.layer_ = null;
  }
}
